// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: "Waiting on a reply", both ways. Konvo keeps the questions that are still open in mail,
// WhatsApp and Konvo chats: people waiting on you (they asked, you have not answered) and people
// you are waiting on (you asked, no answer yet). An item closes by itself when the answer arrives.
// The API (konvo/engine/waiting) and the app (features/konvo_waiting) both import these shapes.

import {z} from 'zod';

export const WAITING_DIRECTIONS = ['on_you', 'on_them'] as const;
export type WaitingDirection = (typeof WAITING_DIRECTIONS)[number];

export const WAITING_SOURCES = ['mail', 'whatsapp', 'konvo'] as const;
export type WaitingSource = (typeof WAITING_SOURCES)[number];

export const WAITING_STATUSES = ['open', 'answered', 'done', 'dropped'] as const;
export type WaitingStatus = (typeof WAITING_STATUSES)[number];

/**
 * Why the item comes up when it does. `default`: about one working day of silence. `learned`:
 * this person usually answers (or you usually answer them) within `typical_hours`, from `samples`
 * answered questions. `later`: you put it off before, so Konvo waits longer for this person.
 * `reminder`: a reminder about the same thing sets the time.
 */
export const WAITING_BASES = ['default', 'learned', 'later', 'reminder'] as const;
export type WaitingBasis = (typeof WAITING_BASES)[number];

export const WaitingConversationSchema = z.object({
	source: z.enum(WAITING_SOURCES),
	/** Mail: Graph conversationId. WhatsApp: the chat jid. Konvo: the channel id. */
	id: z.string().max(512),
	/** The message to open: the ask itself, or the newest one Konvo saw in the conversation. */
	message_id: z.string().max(512),
	/** Konvo only: the guild the channel is in (null for DMs and groups). */
	guild_id: z.string().max(40).nullable().default(null),
	/** Mail subject or chat name, for lists. */
	title: z.string().max(240).default(''),
});
export type WaitingConversation = z.infer<typeof WaitingConversationSchema>;

export const WaitingPersonSchema = z.object({
	name: z.string().max(160),
	/** Stable per person and channel: "mail:<address>", "wa:<jid>" or "user:<id>". */
	key: z.string().max(400),
	/** Mail address, when known. */
	address: z.string().max(320).nullable().default(null),
	/** A WhatsApp chat with this person, so a mail nudge can go by WhatsApp instead. */
	whatsapp_jid: z.string().max(200).nullable().default(null),
	/** A Konvo user id, for avatars. */
	user_id: z.string().max(40).nullable().default(null),
});
export type WaitingPerson = z.infer<typeof WaitingPersonSchema>;

export const WaitingItemSchema = z.object({
	id: z.string().max(40),
	direction: z.enum(WAITING_DIRECTIONS),
	conversation: WaitingConversationSchema,
	person: WaitingPersonSchema,
	/** A short English line of what is asked, e.g. "Coordinates of the four new depots". */
	what: z.string().max(200),
	/** The words of the ask, trimmed. */
	quote: z.string().max(500),
	asked_at: z.string().max(40),
	/** When it comes up (the reminder's time when there is one). */
	due_at: z.string().max(40),
	status: z.enum(WAITING_STATUSES),
	/** Put off until then ("Later", "Remind me Fri"). */
	snoozed_until: z.string().max(40).nullable().default(null),
	/** Nudges sent so far, and the last one. */
	nudges: z.number().int().min(0).default(0),
	nudged_at: z.string().max(40).nullable().default(null),
	basis: z.enum(WAITING_BASES),
	/** learned / later: the usual time to an answer, in working hours. */
	typical_hours: z.number().min(0).nullable().default(null),
	samples: z.number().int().min(0).default(0),
	/** The ask's own message (WhatsApp message id, Konvo message id or Graph id), so a reply can quote it. */
	ask_message_id: z.string().max(512).nullable().optional(),
	/** A reminder about the same thing, merged into this item. */
	reminder: z
		.object({id: z.string().max(80), at: z.string().max(40)})
		.nullable()
		.default(null),
});
export type WaitingItem = z.infer<typeof WaitingItemSchema>;

/** GET /konvo/assistant/waiting */
export const WaitingListResponseSchema = z.object({
	/** Tracking is on (Settings → Assistant → Proactive → Waiting on a reply) and the Assistant is connected. */
	enabled: z.boolean(),
	/** Open items, oldest ask first. Snoozed ones are included with `snoozed_until`. */
	items: z.array(WaitingItemSchema),
	/** Items that closed because the answer came, in the last 7 days. */
	answered_week: z.number().int().min(0),
	/** Working hours of silence before an item comes up when Konvo knows nothing about the person. */
	default_hours: z.number().min(0),
});
export type WaitingListResponse = z.infer<typeof WaitingListResponseSchema>;

export const WAITING_ACTIONS = ['done', 'later', 'not_waiting', 'nudged', 'replied'] as const;
export type WaitingAction = (typeof WAITING_ACTIONS)[number];

/** POST /konvo/assistant/waiting/:id/action */
export const WaitingActionRequestSchema = z.object({
	action: z.enum(WAITING_ACTIONS),
	/** later: until when (default: the next working morning). */
	until: z.string().max(40).optional(),
});
export type WaitingActionRequest = z.infer<typeof WaitingActionRequestSchema>;

export const WAITING_VIAS = ['mail', 'whatsapp', 'konvo'] as const;
export type WaitingVia = (typeof WAITING_VIAS)[number];

/**
 * POST /konvo/assistant/waiting/:id/draft. Without `instruction`: someone waiting on you gets 2–3
 * answer choices (kept per item until a newer message arrives; `fresh` asks for different ones),
 * and a nudge gets one draft. With `instruction`: `current` changed as asked ("Make it shorter"),
 * or, when `current` is empty, a reply written from the instruction ("say no, maybe next term").
 */
export const WaitingDraftRequestSchema = z.object({
	via: z.enum(WAITING_VIAS).optional(),
	fresh: z.boolean().optional(),
	instruction: z.string().max(500).optional(),
	current: z.string().max(4000).optional(),
});
export type WaitingDraftRequest = z.infer<typeof WaitingDraftRequestSchema>;

/** One answer Konvo offers: a short chip label and the whole message in the user's voice. */
export const WaitingDraftChoiceSchema = z.object({
	label: z.string().max(60),
	text: z.string().max(4000),
});
export type WaitingDraftChoice = z.infer<typeof WaitingDraftChoiceSchema>;

export const WaitingDraftResponseSchema = z.object({
	/** The draft (the first choice when there are choices), for older apps. */
	text: z.string().max(4000),
	via: z.enum(WAITING_VIAS),
	/** Someone waiting on you, no instruction: the answers to pick from. */
	choices: z.array(WaitingDraftChoiceSchema).max(3).optional(),
});
export type WaitingDraftResponse = z.infer<typeof WaitingDraftResponseSchema>;

/** POST /konvo/assistant/waiting/:id/send: sends the (edited) draft on that channel. */
export const WaitingSendRequestSchema = z.object({
	text: z.string().min(1).max(4000),
	via: z.enum(WAITING_VIAS),
});
export const WaitingSendResponseSchema = z.object({
	item: WaitingItemSchema.nullable(),
});

/** Pure: is the item due to come up now (open, not snoozed, past its time)? */
export function isWaitingDue(item: WaitingItem, now: number): boolean {
	if (item.status !== 'open') return false;
	if (item.snoozed_until && Date.parse(item.snoozed_until) > now) return false;
	return Date.parse(item.due_at) <= now;
}
